import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useState } from 'react';
import ConfirmationModal from './ConfirmationModal';
import { useTheme } from '@mui/material/styles';
import { Box, IconButton, Tooltip } from '@mui/material';
import { MRT_Localization_EN } from 'mantine-react-table/locales/en';

const CommonMantineTable = ({
  columns = [],
  data = [],
  onEdit,
  onDelete,
  enableRowSelection = true,
  stickyHeader = true,
  pageSize = 10,
  tableStyles = {}, // 🎨 allows parent to override palette colors, widths, etc.
}) => {
  const theme = useTheme();

  // 🔧 Defaults (can be overridden by parent)
  const {
    headerBg = theme.palette.mode === 'light' ? '#EDEBFF' : '#3A3A4D',
    rowStripeLight = '#FAF9FF',
    rowStripeDark = '#3B3B4B',
    tableBg = theme.palette.mode === 'light' ? '#FFFFFF' : '#2E2E3E',
    actionEditColor = theme.palette.primary.main,
    actionDeleteColor = theme.palette.error.main,
    tableMaxHeight = '70vh',
    columnMinWidth = '140px',
  } = tableStyles;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  const openConfirmationModal = (id) => {
    setRowToDelete(id);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (rowToDelete !== null && onDelete) onDelete(rowToDelete);
    setRowToDelete(null);
    setIsModalOpen(false);
  };

  // ✅ Add edit/delete column dynamically
  const enhancedColumns = [
    ...columns.map((col) => ({
      ...col,
      size: col.size || columnMinWidth,
    })),
    ...(onEdit || onDelete
      ? [
          {
            accessorKey: 'actions',
            header: 'Actions',
            enableSorting: false,
            enableHiding: false,
            size: 100,
            Cell: ({ row }) => (
              <Box sx={{ display: 'flex', gap: '0.8vw', justifyContent: 'center' }}>
                {onEdit && (
                  <Tooltip title="Edit" arrow>
                    <IconButton
                      size="small"
                      sx={{
                        color: actionEditColor,
                        backgroundColor:
                          theme.palette.mode === 'light'
                            ? 'rgba(192,132,252,0.15)'
                            : 'rgba(216,180,254,0.15)',
                        borderRadius: '50%',
                        transition: 'all 0.25s ease',
                        '&:hover': {
                          backgroundColor:
                            theme.palette.mode === 'light'
                              ? 'rgba(192,132,252,0.3)'
                              : 'rgba(216,180,254,0.3)',
                          transform: 'scale(1.08)',
                        },
                      }}
                      onClick={() => {
                        // ✅ Return full row object when edit clicked
                        if (onEdit) onEdit(row.original);
                      }}
                    >
                      <FontAwesomeIcon icon={faEdit} size="sm" />
                    </IconButton>
                  </Tooltip>
                )}

                {onDelete && (
                  <Tooltip title="Delete" arrow>
                    <IconButton
                      size="small"
                      sx={{
                        color: actionDeleteColor,
                        backgroundColor:
                          theme.palette.mode === 'light'
                            ? 'rgba(255,0,0,0.1)'
                            : 'rgba(255,255,255,0.1)',
                        borderRadius: '50%',
                        transition: 'all 0.25s ease',
                        '&:hover': {
                          backgroundColor:
                            theme.palette.mode === 'light'
                              ? 'rgba(255,0,0,0.25)'
                              : 'rgba(255,255,255,0.25)',
                          transform: 'scale(1.08)',
                        },
                      }}
                      onClick={() => openConfirmationModal(row.original.id)}
                    >
                      <FontAwesomeIcon icon={faTrash} size="sm" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            ),
          },
        ]
      : []),
  ];

  const table = useMantineReactTable({
    columns: enhancedColumns,
    data,
    enableRowSelection,
    enableStickyHeader: stickyHeader,
    enableColumnResizing: true,
    enableColumnHiding: true,
    enableGlobalFilter: true,
    enableSorting: true,
    enablePagination: true,
    initialState: { pagination: { pageSize } },
    localization: MRT_Localization_EN,

    // 🎨 Main table container
    mantinePaperProps: {
      sx: {
        borderRadius: '1vw',
        backgroundColor: tableBg,
        boxShadow:
          theme.palette.mode === 'light'
            ? '0px 4px 20px rgba(192,132,252,0.15)'
            : '0px 4px 20px rgba(0,0,0,0.4)',
        overflow: 'hidden',
        transition: 'background 0.3s ease',
      },
    },

    mantineTableContainerProps: {
      sx: {
        overflowX: 'auto',
        overflowY: 'auto',
        width: '100%',
        maxHeight: tableMaxHeight,
      },
    },

    mantineTableProps: {
      sx: {
        minWidth: 'max-content',
        tableLayout: 'auto',
        fontFamily: 'Nunito Sans, sans-serif',
        backgroundColor: tableBg,
        width: '100%',
      },
    },

    // 🎨 Striped and hover styles
    mantineTableBodyRowProps: ({ row }) => ({
      sx: {
        transition: 'background 0.3s ease',
        backgroundColor:
          row.index % 2 === 0
            ? theme.palette.mode === 'light'
              ? rowStripeLight
              : rowStripeDark
            : 'transparent',
        '&:hover': {
          backgroundColor:
            theme.palette.mode === 'light'
              ? 'rgba(192,132,252,0.1)'
              : 'rgba(216,180,254,0.15)',
        },
      },
    }),

    mantineTableHeadCellProps: {
      sx: {
        whiteSpace: 'nowrap',
        backgroundColor: headerBg,
        borderBottom: `1px solid ${theme.palette.divider}`,
        fontWeight: 700,
        fontSize: '1rem',
        color: theme.palette.text.primary,
      },
    },

    mantineTableBodyCellProps: {
      sx: {
        padding: '0.9vw',
        color:
          theme.palette.mode === 'light'
            ? theme.palette.text.secondary
            : '#FFFFFF',
        fontSize: '0.95rem',
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
    },

    mantineTopToolbarProps: {
      sx: {
        backgroundColor:
          theme.palette.mode === 'light' ? '#F8F7FF' : '#3A3A4D',
        padding: '0.5rem 1rem',
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
    },
  });

  return (
    <Box sx={{ width: '100%', overflowX: 'auto', mb: '2rem' }}>
      <MantineReactTable table={table} />
      {onDelete && (
        <ConfirmationModal
          open={isModalOpen}
          title="Delete Item"
          message="Are you sure you want to delete this item? This action cannot be undone."
          onConfirm={handleConfirmDelete}
          onCancel={() => setIsModalOpen(false)}
          confirmLabel="Confirm"
          cancelLabel="Cancel"
        />
      )}
    </Box>
  );
};

export default CommonMantineTable;
